
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/ui/PageHeader';
import Layout from '@/components/layout/Layout';
import Stats from '@/components/dashboard/Stats';
import { getStats } from '@/utils/mockData';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';

// Function to get real dashboard data
const fetchRealDashboardData = async () => {
  // In a real app, you would call an API here
  // Simulate API call with a small delay
  return new Promise<{
    totalUsers: number;
    activeUsersToday: number;
    averageSessionDuration: string;
    newSignupsThisWeek: number;
    lastSignup: string;
    lastScheduleChange: string;
  }>((resolve) => {
    setTimeout(() => {
      const users = JSON.parse(localStorage.getItem('roosterapp_users') || '[]');
      
      resolve({
        totalUsers: users.length,
        activeUsersToday: Math.floor(users.length * 0.7), // Simulate active users
        averageSessionDuration: '24 min',
        newSignupsThisWeek: Math.floor(users.length * 0.3), // Simulate new signups
        lastSignup: '2 uur geleden',
        lastScheduleChange: '15 minuten geleden',
      });
    }, 500);
  });
};

const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { toast } = useToast();
  const { user } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboardData'],
    queryFn: fetchRealDashboardData
  });

  const statsData = data ? getStats() : null;

  useEffect(() => {
    if (isError) {
      toast({
        title: "Fout bij het ophalen van gegevens",
        description: "Er is een probleem opgetreden bij het laden van de dashboardgegevens. Probeer het later opnieuw.",
        variant: "destructive",
      });
    }
  }, [isError, toast]);

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (isError) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          Fout bij het laden van gegevens.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader 
        title={`Dashboard - Welkom ${user?.name || 'Gebruiker'}`} 
        description="Overzicht van uw RoosterApp" 
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overzicht</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="reports">Rapporten</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="space-y-4">
          {statsData && (
            <>
              <Stats 
                totalEmployees={statsData.totalEmployees}
                scheduledShifts={statsData.scheduledShifts}
                hoursScheduled={statsData.hoursScheduled}
                departments={statsData.departments}
                upcomingTimeOffRequests={statsData.upcomingTimeOffRequests}
                openShifts={statsData.openShifts}
              />
              <Card>
                <CardHeader>
                  <CardTitle>Recente Activiteit</CardTitle>
                  <CardDescription>Overzicht van de laatste gebruikersactiviteiten.</CardDescription>
                </CardHeader>
                <CardContent>
                  <p>Laatste aanmelding: {data?.lastSignup || 'Geen recente aanmeldingen'}</p>
                  <p>Laatste roosterwijziging: {data?.lastScheduleChange || 'Geen recente wijzigingen'}</p>
                  <p>Totaal aantal gebruikers: {data?.totalUsers || 0}</p>
                  <p>Actieve gebruikers vandaag: {data?.activeUsersToday || 0}</p>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
        <TabsContent value="analytics">
          <Card>
            <CardHeader>
              <CardTitle>Gebruikersstatistieken</CardTitle>
              <CardDescription>Gedetailleerde informatie over gebruikersgedrag.</CardDescription>
            </CardHeader>
            <CardContent>
              <p>Hier komen de gebruikersstatistieken.</p>
              {user?.role === 'admin' && (
                <div className="mt-4 p-4 bg-amber-50 rounded-md border border-amber-200">
                  <h3 className="font-medium text-amber-800">Admin-only informatie</h3>
                  <p className="text-amber-700 text-sm mt-1">Deze sectie is alleen zichtbaar voor beheerders.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="reports">
          <Card>
            <CardHeader>
              <CardTitle>Rapportage</CardTitle>
              <CardDescription>Genereer rapporten op basis van uw gegevens.</CardDescription>
            </CardHeader>
            <CardContent>
              <p>Hier komen de rapportage-opties.</p>
              <Button onClick={() => {
                toast({
                  title: "Export gestart",
                  description: "Uw gegevens worden geëxporteerd en zullen binnenkort beschikbaar zijn.",
                });
              }}>Exporteer Data</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </Layout>
  );
};

export default Dashboard;
